import { ErrorHandler, Injectable, inject } from '@angular/core';

import { HelperToastControllerService } from '../helpers/helper-toast-controller.service';
import { HelperDebugLoggerControllerService } from '../helpers/helper-debug-logger-controller.service';

@Injectable({
  providedIn: 'root'
})
export class GlobalErrorHandler implements ErrorHandler {
  private toastService = inject(HelperToastControllerService);
  private debugLogger = inject(HelperDebugLoggerControllerService);

  handleError(error: unknown): void {
    console.error('Unhandled Application Error:', error);

    const errObj = error as Record<string, any> | null | undefined;
    const errorMessage = errObj?.['message'] || errObj?.['error']?.['message'] || 'An unexpected error occurred. Please try again.';
    
    this.debugLogger.addLog('error', 'Unhandled JS Exception', {
      message: errorMessage,
      stack: errObj?.['stack'],
    });

    // Automatic Chunk Load Recovery for Web deployments
    if (this.isChunkLoadError(errorMessage)) {
      this.handleChunkLoadRecovery();
      return;
    }

    // Asynchronously trigger Toast notification without blocking execution
    this.toastService.presentError(errorMessage).catch(toastErr => {
      console.error('Failed to present error toast:', toastErr);
    });
  }

  private isChunkLoadError(message: string): boolean {
    return (
      /Loading chunk/i.test(message) ||
      /Failed to fetch dynamically imported module/i.test(message) ||
      /Importing a module script failed/i.test(message) ||
      message.includes('ChunkLoadError')
    );
  }

  private handleChunkLoadRecovery(): void {
    const STORAGE_KEY = 'bm_chunk_reload_timestamp';
    const now = Date.now();
    const lastReloadStr = typeof window !== 'undefined' ? window.sessionStorage.getItem(STORAGE_KEY) : null;
    const lastReload = lastReloadStr ? parseInt(lastReloadStr, 10) : 0;

    // Loop protection: only reload if last auto-reload was > 30 seconds ago
    if (!lastReload || now - lastReload > 30_000) {
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(STORAGE_KEY, String(now));
        this.reloadWindow();
      }
    } else {
      this.toastService.presentError('New app version available. Please refresh the page.').catch(() => {});
    }
  }

  protected reloadWindow(): void {
    if (typeof window !== 'undefined' && window.location) {
      window.location.reload();
    }
  }
}
